import type { WorkplaceRequestReservationFieldConfig } from "@gettactic/api";
import {
  EMPTY_RESERVATION_FORM_VALUE,
  type ReservationFieldFormValue
} from "@gettactic/helpers/src/workplace-requests/reservationFieldValue";
import { addMinutes, format, isSameDay, startOfDay } from "date-fns";

/**
 * The days and hours a request was opened for: the map's selected date and
 * time, or the reservation a request is being added to.
 *
 * Every date holds the office's wall-clock time in its local fields, the way
 * the map and the reserve form keep them — never an instant to convert.
 */
export type ReservationWhen = {
  /** The days, in any order; only their calendar day is read. */
  days: Date[];
  start: Date;
  /** `null` when the opener has no end, e.g. a start and nothing else. */
  end: Date | null;
};

/** The form the time picker holds its value in, e.g. "09:00 AM". */
const toPickerTime = (date: Date): string => format(date, "hh:mm a");

const uniqueSortedDays = (days: Date[]): Date[] => {
  const byDay = new Map<number, Date>();
  for (const day of days) {
    const midnight = startOfDay(day);
    byDay.set(midnight.getTime(), midnight);
  }
  return [...byDay.values()].sort(
    (left, right) => left.getTime() - right.getTime()
  );
};

/**
 * A reservation question's first answer, taken from where the request was
 * opened, so the requester starts from the days and hours they were already
 * looking at instead of picking them again.
 *
 * Kept to what the question collects: a single-day question gets the first
 * day only. An end on another day than the start, or not after it, is left
 * out rather than guessed — the picker has one window for every day, and
 * it cannot hold one that runs past midnight.
 */
export const reservationValueFromWhen = (
  when: ReservationWhen,
  config: Pick<WorkplaceRequestReservationFieldConfig, "multi_day_enabled">
): ReservationFieldFormValue => {
  const days = uniqueSortedDays(when.days);
  const hasEnd =
    !!when.end &&
    isSameDay(when.start, when.end) &&
    when.end.getTime() > when.start.getTime();
  return {
    ...EMPTY_RESERVATION_FORM_VALUE,
    dates: config.multi_day_enabled ? days : days.slice(0, 1),
    startTime: toPickerTime(when.start),
    endTime: hasEnd && when.end ? toPickerTime(when.end) : ""
  };
};

/**
 * The reserve form's days and hours as a {@link ReservationWhen}: `time`
 * holds the hour every day starts at and `duration` how long it lasts, in
 * minutes. `null` while the form has no day or no time yet.
 */
export const reservationWhenFromReserveForm = (values: {
  weekDates?: Date[] | null;
  time?: Date | null;
  duration?: { value: number } | null;
}): ReservationWhen | null => {
  const { weekDates, time, duration } = values;
  if (!weekDates?.length || !time) return null;
  return {
    days: weekDates,
    start: time,
    end:
      duration && duration.value > 0 ? addMinutes(time, duration.value) : null
  };
};
